import {useStreamStore} from '@/stores/streamStore'
import {sleep} from '@/utils'

const getTrackCapabilities = (videoTrack: MediaStreamTrack) => {
  // getCapabilities is missing in older Firefox and returns {} for tracks that do not come from local hardware
  try {
    const capabilities = videoTrack.getCapabilities?.()
    if (capabilities?.width?.max && capabilities?.height?.max) return capabilities
  } catch (error) {}
  return null
}

const getCurrentResolution = (videoTrack: MediaStreamTrack) => {
  const {width: originWidth = 0, height: originHeight = 0} = videoTrack.getSettings()
  return {width: Math.max(originWidth, originHeight), height: Math.min(originWidth, originHeight)}
}

const getMaxResolution = (videoTrack: MediaStreamTrack) => {
  const capabilities = getTrackCapabilities(videoTrack)!
  const maxWidth = capabilities.width!.max!
  const maxHeight = capabilities.height!.max!
  return {width: Math.max(maxWidth, maxHeight, 1920), height: Math.min(maxWidth, maxHeight, 1080)}
}

const getMaxResolutionFromCurrent = (videoTrack: MediaStreamTrack) => {
  const {width: maxWidth, height: maxHeight} = getMaxResolution(videoTrack)
  const {width: cameraWidth, height: cameraHeight} = getCurrentResolution(videoTrack)
  const ratio = Math.min(maxWidth / cameraWidth, maxHeight / cameraHeight)
  return {width: Math.floor(cameraWidth * ratio), height: Math.floor(cameraHeight * ratio)}
}

const isMaxResolution = (videoTrack: MediaStreamTrack) => {
  // Without capability data a resolution bump cannot be attempted safely
  if (!getTrackCapabilities(videoTrack)) return true
  const {width: maxWidth, height: maxHeight} = getMaxResolution(videoTrack)
  const {width: cameraWidth, height: cameraHeight} = getCurrentResolution(videoTrack)
  return cameraWidth === maxWidth || cameraHeight === maxHeight
}

const createSnapshot = (videoElement: HTMLVideoElement, width: number, height: number, isThumbnail: boolean) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d')!.drawImage(videoElement, 0, 0, width, height)
  // 0.92 instead of 1: about a fifth of the size (measured on 1080p photos, 37-50 dB PSNR), which the phone has to
  // upload during the call; 0.95 or 0.97 add only 0.6-2 dB for 40-80 % more bytes
  return canvas.toDataURL('image/jpeg', isThumbnail ? 0.1 : 0.92)
}

export const useLocalSnapshot = () => {
  const streamStore = useStreamStore()

  const turnTorchOn = async (track?: MediaStreamTrack) => {
    try {
      await track?.applyConstraints({advanced: [{torch: true} as any]})
    } catch (error) {
      console.warn('[getSnapshot] Could not turn the torch back on:', error)
    }
  }

  // The camera is bumped to its maximum resolution through streamStore's camResolution: the constraint change makes
  // useUserMedia open a new stream (the same camera cannot be opened twice at different resolutions), which the
  // strategies move their sender/producer to, and the local preview follows. camResolution is always reset, which
  // also revives the camera when a step failed
  const getSnapshot = async () => {
    const localVideo = document.getElementById('local-video') as HTMLVideoElement | null
    if (!localVideo) throw new Error('local-video element not found')
    const currentTrack = streamStore.camStream?.getVideoTracks()[0]
    const settings = currentTrack?.getSettings() ?? {}
    const isLandscape = screen.orientation.type.includes('landscape')

    // No usable camera track (missing, ended after an earlier failure, or not hardware-backed): capture whatever
    // the video element currently renders
    if (!currentTrack || currentTrack.readyState === 'ended' || !settings.width) {
      const width = localVideo.videoWidth || 1280
      const height = localVideo.videoHeight || 720
      return {
        thumbnailData: createSnapshot(localVideo, width, height, true),
        data: createSnapshot(localVideo, width, height, false),
        width,
        height,
      }
    }

    // The torch belongs to the track: the reopened camera starts with it off
    const torchOn = (settings as any).torch === true
    const {width: cameraWidth, height: cameraHeight} = getCurrentResolution(currentTrack)
    let imageWidth = isLandscape ? cameraWidth : cameraHeight
    let imageHeight = isLandscape ? cameraHeight : cameraWidth

    // A bridge camera has no constraints to raise
    if (!currentTrack.enabled || streamStore.isAndroidDevice(streamStore.selectedCameraId) || isMaxResolution(currentTrack)) {
      return {
        thumbnailData: createSnapshot(localVideo, imageWidth, imageHeight, true),
        data: createSnapshot(localVideo, imageWidth, imageHeight, false),
        width: imageWidth,
        height: imageHeight,
      }
    }

    let data: string
    let thumbnailData: string
    try {
      streamStore.camResolution = getMaxResolutionFromCurrent(currentTrack)
      const snapshotStream = await streamStore.nextCamStream()
      const snapshotTrack = snapshotStream?.getVideoTracks()[0]
      await localVideo.play().catch(() => {})
      // Before the wait, so the exposure settles with the light on
      if (torchOn) await turnTorchOn(snapshotTrack)
      await sleep(1500)

      // Draw at the resolution the snapshot track actually delivers
      const {width: snapshotWidth, height: snapshotHeight} = getCurrentResolution(snapshotTrack ?? currentTrack)
      imageWidth = isLandscape ? snapshotWidth : snapshotHeight
      imageHeight = isLandscape ? snapshotHeight : snapshotWidth
      thumbnailData = createSnapshot(localVideo, imageWidth, imageHeight, true)
      data = createSnapshot(localVideo, imageWidth, imageHeight, false)
    } finally {
      streamStore.camResolution = null
      try {
        const restoredStream = await streamStore.nextCamStream()
        await localVideo.play().catch(() => {})
        if (torchOn) await turnTorchOn(restoredStream?.getVideoTracks()[0])
      } catch (restoreError) {
        console.error('[getSnapshot] Failed to restore camera after snapshot:', restoreError)
      }
    }

    return {thumbnailData, data, width: imageWidth, height: imageHeight}
  }

  return {getSnapshot}
}
